// HTTP Client Service
// Centralized axios configuration and request/response interceptors
import axios from "axios";
import { API_CONFIG, getV1EndpointUrl } from "../config/api.js";
import { handleTokenExpiry, shouldSkipAutoLogout } from "../utils/authUtils.js";
import { hasLocalStorage, getLocalStorage } from "../utils/browserCheck.js";

console.log('🔧 [API Config] Initializing API clients');
console.log('🔧 [API Config] BASE_URL:', API_CONFIG.BASE_URL);
console.log('🔧 [API Config] BASE_URL_V1:', API_CONFIG.BASE_URL_V1);

// Create axios instance with base configuration
const apiClient = axios.create({
  baseURL: API_CONFIG.BASE_URL,
  timeout: API_CONFIG.TIMEOUT,
  headers: {
    "Content-Type": API_CONFIG.HEADERS.CONTENT_TYPE,
  },
});
console.log('🔧 [API Config] apiClient baseURL:', apiClient.defaults.baseURL);

// Create separate axios instance for v1 endpoints (without /api prefix)
const v1ApiClientBaseUrl = API_CONFIG.BASE_URL_V1 || API_CONFIG.BASE_URL;
const v1ApiClient = axios.create({
  baseURL: v1ApiClientBaseUrl,
  timeout: API_CONFIG.TIMEOUT,
  headers: {
    "Content-Type": API_CONFIG.HEADERS.CONTENT_TYPE,
  },
});
console.log('🔧 [API Config] v1ApiClient baseURL:', v1ApiClient.defaults.baseURL);

// Response interceptor - Handle responses and errors
apiClient.interceptors.response.use(
  (response) => {
    // Debug logging
    if (API_CONFIG.FEATURES.DEBUG) {
      console.log("API Response:", {
        status: response.status,
        url: response.config.url,
        data: response.data,
      });
    }
    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    // Handle 401 Unauthorized - Token expired or invalid
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      const url = originalRequest.url || '';

      // Skip auto-logout only for actual auth endpoints (login, register, etc.)
      // All other endpoints should trigger logout on token expiry
      if (shouldSkipAutoLogout(url)) {
        console.log(`🔑 [api.js] Skipping auto-logout for auth endpoint: ${url}`);
        return Promise.reject(error);
      }

      // Handle token expiry with automatic logout
      console.error("🚨 [api.js] 401 error detected - Token expired or invalid");
      console.error("🚨 [api.js] Failed request URL:", url);
      console.error("🚨 [api.js] Error response:", error.response?.data);
      
      await handleTokenExpiry(`Token expired for endpoint: ${url}`);
      // handleTokenExpiry will redirect, but return a rejected promise to prevent further processing
      return Promise.reject(new Error('Token expired - redirecting to login'));
    }

    return Promise.reject(error);
  }
);

// Function to create a configured axios client
const createAxiosClient = (clientName) => {
  const client = axios.create({
    baseURL: API_CONFIG.ENDPOINTS.BASE_URL,
    timeout: API_CONFIG.FEATURES.TIMEOUT,
    headers: {
      'Content-Type': 'application/json',
    },
  });

  // Response interceptor - Handle responses and errors
  client.interceptors.response.use(
    (response) => {
      if (import.meta.env?.DEV && !(response.config?.url || '').includes('allocation-status')) {
        console.log(`✅ [${clientName} Response] Status ${response.status}: ${response.config.url}`);
      }
      return response;
    },
    async (error) => {
      console.error(`❌ [${clientName} Error] Status ${error.response?.status}: ${error.config?.url}`);
      console.error(`   Message: ${error.response?.data?.message || error.message}`);
      
      const originalRequest = error.config;

      // Handle 401 Unauthorized - Token expired or invalid
      if (error.response?.status === 401 && !originalRequest._retry) {
        originalRequest._retry = true;

        console.log(`🔑 [${clientName}] Handling 401 Unauthorized`);
        console.log(`🔑 [${clientName}] Request URL: ${originalRequest.url}`);
        console.log(`🔑 [${clientName}] Request method: ${originalRequest.method}`);

        // Check if this is a payment-related endpoint - these should NOT redirect to login
        const isPaymentEndpoint = originalRequest.url?.includes('/initiate-payment') || 
                                 originalRequest.url?.includes('/verify-payment') ||
                                 originalRequest.url?.includes('payments/verify');

        // List of endpoints that should not trigger auto-redirect to login
        const noRedirectEndpoints = [
          "/register",
          "/hospital/register",
          "/auth/admin/register-fleet-operator",
          "/send-login-otp",
          "/complete-login",
          "/login",
          "/v1/hospital/auth/login",
          "/profile",
          "/initiate-payment",
          "/verify-payment",
          "/payments/verify",
          "/hospital/bookings",
        ];

        const shouldSkipRedirect = isPaymentEndpoint || noRedirectEndpoints.some((endpoint) =>
          originalRequest.url?.includes(endpoint)
        );

        console.log(`🔑 [${clientName}] Is payment endpoint: ${isPaymentEndpoint}`);
        console.log(`🔑 [${clientName}] Should skip redirect: ${shouldSkipRedirect}`);

        if (shouldSkipRedirect) {
          console.log(`🔑 [${clientName}] ✅ SKIPPING AUTO-REDIRECT - returning error to caller`);
          return Promise.reject(error);
        }

        const url = originalRequest.url || '';
        
        // Skip auto-logout only for actual auth endpoints (login, register, etc.)
        // All other endpoints should trigger logout on token expiry
        if (shouldSkipAutoLogout(url)) {
          console.log(`🔑 [api.js createAxiosClient] Skipping auto-logout for auth endpoint: ${url}`);
          return Promise.reject(error);
        }

        // Check if the error indicates token expiration
        const errorMessage = error.response?.data?.message || '';
        const isTokenExpired = 
          errorMessage.toLowerCase().includes("expired") ||
          errorMessage.toLowerCase().includes("Token has expired") ||
          errorMessage.toLowerCase().includes("invalid token");

        if (isTokenExpired) {
          try {
            // Try to refresh token
            const storage = getLocalStorage();
            if (!storage) {
              throw new Error('localStorage not available');
            }
            const refreshToken = storage.getItem(
              API_CONFIG.STORAGE_KEYS.REFRESH_TOKEN
            );
            if (refreshToken) {
              const userData = storage.getItem(API_CONFIG.STORAGE_KEYS.USER_DATA);
              const isHospitalUser = userData && (
                JSON.parse(userData).role === 'HOSPITAL_USER' || 
                JSON.parse(userData).role === 'hospital'
              );

              let response;
              let newToken;
              let newRefreshToken;

              if (isHospitalUser) {
                const hospitalRefreshUrl = getV1EndpointUrl("/v1/hospital/auth/refresh-token");
                response = await axios.post(
                  hospitalRefreshUrl,
                  { refreshToken },
                  { headers: { "Content-Type": "application/json" } }
                );

                if (response.data.success && response.data.data) {
                  newToken = response.data.data.accessToken;
                  newRefreshToken = response.data.data.refreshToken;
                }
              } else {
                response = await axios.post(
                  `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.AUTH}${API_CONFIG.AUTH_ENDPOINTS.REFRESH}`,
                  { refreshToken },
                  { headers: { "Content-Type": "application/json" } }
                );

                if (response.data.success && response.data.data) {
                  newToken = response.data.data.token || response.data.data.accessToken;
                  newRefreshToken = response.data.data.refreshToken;
                }
              }

              const storage = getLocalStorage();
              if (newToken && storage) {
                storage.setItem(API_CONFIG.STORAGE_KEYS.JWT_TOKEN, newToken);
                if (newRefreshToken) {
                  storage.setItem(API_CONFIG.STORAGE_KEYS.REFRESH_TOKEN, newRefreshToken);
                }

                originalRequest.headers.Authorization = `Bearer ${newToken}`;
                return client(originalRequest);
              }
            }
          } catch (refreshError) {
            console.warn("Token refresh failed:", refreshError.message);
          }
          
          // If refresh failed or no refresh token, handle token expiry
          await handleTokenExpiry(`Token expired for endpoint: ${url}`);
          return Promise.reject(new Error('Token expired - redirecting to login'));
        }

        // For other 401 errors, also logout (invalid/missing token)
        await handleTokenExpiry(`Unauthorized access for endpoint: ${url}`);
        return Promise.reject(new Error('Unauthorized - redirecting to login'));
      }

      return Promise.reject(error);
    }
  );
};

// Function to add interceptors to a client
const addInterceptors = (client, clientName) => {
  // Request interceptor - Add JWT token to headers
  client.interceptors.request.use(
    (config) => {
      // Add JWT token to Authorization header
      const storage = getLocalStorage();
      if (!storage) {
        return config;
      }
      const token = storage.getItem(API_CONFIG.STORAGE_KEYS.JWT_TOKEN);
      console.log(`🔑 [${clientName} Request] Token key: ${API_CONFIG.STORAGE_KEYS.JWT_TOKEN}, Present: ${!!token}`);
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
        console.log(`🔑 [${clientName} Request] ✅ Token added to headers (${token.length} chars)`);
      } else {
        console.warn(`⚠️ [${clientName} Request] ❌ No JWT token in localStorage`);
        const altToken = storage.getItem('token');
        if (altToken) {
          console.warn(`⚠️ [${clientName} Request] Using fallback 'token' key`);
          config.headers.Authorization = `Bearer ${altToken}`;
        } else {
          console.warn(`⚠️ [${clientName} Request] ❌ No fallback token - request WILL be unauthenticated`);
        }
      }
      return config;
    },
    (error) => {
      console.error(`❌ [${clientName} Request Error]:`, error);
      return Promise.reject(error);
    }
  );

  // Response interceptor - Handle responses and errors
  client.interceptors.response.use(
    (response) => {
      if (import.meta.env?.DEV && !(response.config?.url || '').includes('allocation-status')) {
        console.log(`✅ [${clientName} Response] Status ${response.status}: ${response.config.url}`);
      }
      return response;
    },
    async (error) => {
      console.error(`❌ [${clientName} Error] Status ${error.response?.status}: ${error.config?.url}`);
      console.error(`   Message: ${error.response?.data?.message || error.message}`);

      const originalRequest = error.config;

      // Handle 401 Unauthorized - Token expired or invalid
      if (error.response?.status === 401 && !originalRequest._retry) {
        originalRequest._retry = true;

        const url = originalRequest.url || '';
        console.log(`🔑 [${clientName}] Handling 401 Unauthorized`);
        console.log(`🔑 [${clientName}] Request URL: ${url}`);

        // Skip auto-logout only for actual auth endpoints (login, register, etc.)
        // All other endpoints should trigger logout on token expiry
        if (shouldSkipAutoLogout(url)) {
          console.log(`🔑 [${clientName}] Skipping auto-logout for auth endpoint: ${url}`);
          return Promise.reject(error);
        }

        // Handle token expiry with automatic logout
        const errorMessage = error.response?.data?.message || '';
        const isTokenExpired = 
          errorMessage.toLowerCase().includes('expired') ||
          errorMessage.toLowerCase().includes('token has expired') ||
          errorMessage.toLowerCase().includes('invalid token');

        console.error(`🚨 [${clientName}] 401 error - ${isTokenExpired ? 'Token expired' : 'Unauthorized'}`);
        await handleTokenExpiry(`Token expired for endpoint: ${url}`);
        return Promise.reject(new Error('Token expired - redirecting to login'));
      }

      return Promise.reject(error);
    }
  );
};

// Add interceptors to both clients
console.log('🔧 [API Config] Adding interceptors...');
addInterceptors(apiClient, 'RegularAPI');
addInterceptors(v1ApiClient, 'V1API');
console.log('🔧 [API Config] Interceptors added successfully');

// Generic API methods for regular endpoints
export const api = {
  get: (url, config = {}) => apiClient.get(url, config),
  post: (url, data, config = {}) => apiClient.post(url, data, config),
  put: (url, data, config = {}) => apiClient.put(url, data, config),
  patch: (url, data, config = {}) => apiClient.patch(url, data, config),
  delete: (url, config = {}) => apiClient.delete(url, config),
};

// Generic API methods for v1 endpoints
export const v1Api = {
  get: (url, config = {}) => v1ApiClient.get(url, config),
  post: (url, data, config = {}) => v1ApiClient.post(url, data, config),
  put: (url, data, config = {}) => v1ApiClient.put(url, data, config),
  patch: (url, data, config = {}) => v1ApiClient.patch(url, data, config),
  delete: (url, config = {}) => v1ApiClient.delete(url, config),
};

// Debug function to check current auth status
export const debugAuthStatus = () => {
  const storage = getLocalStorage();
  if (!storage) {
    console.log('🔍 ===== AUTHENTICATION DEBUG INFO =====');
    console.log('⚠️ localStorage not available (likely in Node.js/build environment)');
    console.log('🔍 =====================================');
    return;
  }
  
  console.log('🔍 ===== AUTHENTICATION DEBUG INFO =====');
  console.log('📦 API Configuration:');
  console.log('  - BASE_URL:', API_CONFIG.BASE_URL);
  console.log('  - BASE_URL_V1:', API_CONFIG.BASE_URL_V1);
  console.log('  - STORAGE_KEY:', API_CONFIG.STORAGE_KEYS.JWT_TOKEN);
  
  console.log('🔐 Tokens in localStorage:');
  const tokenKeys = ['aps_jwt_token', 'jwt_token', 'token', 'auth_token', 'accessToken'];
  tokenKeys.forEach(key => {
    const val = storage.getItem(key);
    if (val) {
      console.log(`  ✅ ${key}: ${val.substring(0, 50)}...`);
    }
  });
  
  const primaryToken = storage.getItem(API_CONFIG.STORAGE_KEYS.JWT_TOKEN);
  console.log('👤 User data:', storage.getItem('aps_user_data') ? '✅' : '❌');
  console.log('🏥 Hospital data:', storage.getItem('aps_hospital_data') ? '✅' : '❌');
  
  console.log('🧪 Test request headers that would be sent:');
  if (primaryToken) {
    console.log('  Authorization: Bearer ' + primaryToken.substring(0, 50) + '...');
  } else {
    console.log('  ❌ Authorization header: NONE - request would be unauthenticated');
  }
  console.log('🔍 =====================================');
};

// Generic error handler
export const handleApiError = (error) => {
  if (error.response) {
    const errorObj = {
      success: false,
      message: error.response.data?.message || "An error occurred",
      data: error.response.data?.data || null,
      status: error.response.status,
    };
    if (error.response.data?.errors) {
      errorObj.errors = error.response.data.errors;
    }
    if (error.response.data?.type) {
      errorObj.type = error.response.data.type;
    }
    errorObj.response = error.response;
    return errorObj;
  } else if (error.request) {
    return {
      success: false,
      message: "Network error - please check your connection",
      data: null,
      status: 0,
    };
  } else {
    return {
      success: false,
      message: error.message || "An unexpected error occurred",
      data: null,
      status: 0,
    };
  }
};

// Generic success handler
export const handleApiSuccess = (response) => {
  return {
    success: true,
    message: response.data?.message || "Success",
    data: response.data?.data !== undefined ? response.data.data : response.data,
    meta: response.data?.meta,
    status: response.status,
  };
};

// Export the clients
export { apiClient, v1ApiClient };
